(() => {
  'use strict';
  if (window.YoLinkFaqController) {
    window.YoLinkFaqController.scan();
    return;
  }
  const instances = new Map();
  function mount(root) {
    if (instances.has(root)) return;
    const search = root.querySelector('[data-yl-faq-search]');
    const input = search?.querySelector('input');
    const nav = root.querySelector('[data-yl-faq-topics]');
    const list = root.querySelector('.yl-faq-list');
    const empty = root.querySelector('.yl-faq-empty');
    const status = root.querySelector('[data-yl-faq-status]');
    const originals = [...root.querySelectorAll('details.yl-faq-item')];
    const links = [...(nav?.querySelectorAll('[data-yl-faq-topic]') || [])];
    // Leave the readable native content in place if a merchant's markup is incomplete.
    if (!input || !list || !empty || !status || !links.length || !originals.length ||
        originals.some(item => !item.querySelector('summary') || !item.querySelector('.yl-faq-answer'))) return;

    const controller = new AbortController(), options = { signal: controller.signal };
    let frame;
    const groups = links.map(link => {
      const panel = document.createElement('section');
      panel.className = 'yl-faq-topic';
      panel.id = link.hash.slice(1);
      const title = document.createElement('h2');
      title.id = panel.id + '-title';
      title.className = 'yl-faq-topic-title';
      title.textContent = link.textContent;
      title.tabIndex = -1;
      panel.setAttribute('aria-labelledby', title.id);
      panel.append(title);
      return { key: link.dataset.ylFaqTopic, legacy: (link.dataset.ylFaqLegacy || '').split(' '), link, panel, title, entries: [] };
    });
    const entries = originals.map((original, index) => {
      const summary = original.querySelector('summary');
      const answer = original.querySelector('.yl-faq-answer');
      // Published native Page IDs predate topic settings. Match those stable IDs,
      // never question wording or the current display order. New blocks own a topic.
      const legacy = summary.id.match(/^yl-faq-question-yolink-storefront-faq-(\d+)$/)?.[1];
      const group = groups.find(g => g.key === original.dataset.ylFaqCategory) ||
        groups.find(g => legacy !== undefined && g.legacy.includes(legacy)) || groups.at(-1);
      const article = document.createElement('article');
      for (const attr of original.attributes) if (!['open', 'hidden'].includes(attr.name)) article.setAttribute(attr.name, attr.value);
      const heading = document.createElement('h3');
      heading.id = summary.id || root.id + '-question-' + index;
      heading.className = 'yl-faq-question';
      heading.tabIndex = -1;
      heading.append(...[...summary.childNodes].map(node => node.cloneNode(true)));
      article.setAttribute('aria-labelledby', heading.id);
      article.append(heading, answer);
      group.panel.append(article);
      const entry = { original, article, answer, group };
      group.entries.push(entry);
      return entry;
    });
    const available = groups.filter(group => group.entries.length);
    list.replaceChildren(...available.map(group => group.panel));
    groups.forEach(group => { group.link.parentElement.hidden = !group.entries.length; });
    let active = available[0];
    function render() {
      const query = input.value.trim().toLocaleLowerCase();
      root.classList.toggle('yl-faq-searching', Boolean(query));
      let visible = 0;
      for (const group of groups) {
        let matches = 0;
        for (const entry of group.entries) {
          entry.article.hidden = query ? !entry.article.textContent.toLocaleLowerCase().includes(query) : group !== active;
          if (!entry.article.hidden) matches++;
        }
        group.panel.hidden = matches === 0;
        if (!query && group === active) group.link.setAttribute('aria-current', 'true');
        else group.link.removeAttribute('aria-current');
        visible += matches;
      }
      empty.hidden = visible > 0;
      status.textContent = query ? visible + (visible === 1 ? ' matching question' : ' matching questions') : '';
    }
    function select(group) {
      input.value = '';
      active = group;
      render();
    }
    function reveal(target, scroll = false) {
      const group = available.find(g => g.panel === target || g.panel.contains(target));
      if (!group) return;
      select(group);
      if (scroll) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!controller.signal.aborted) target.scrollIntoView({ block: 'start' });
        });
      }
    }
    function openHash() {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (!id) { select(available[0]); return; }
      const target = id && document.getElementById(id);
      if (target) reveal(target, true);
    }
    nav.addEventListener('click', event => {
      const link = event.target.closest('[data-yl-faq-topic]');
      const group = available.find(g => g.link === link);
      if (!group || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      // Keep shareable topic fragments and Back/Forward history while focusing
      // the answer heading, including reselecting the same topic after a search.
      if (window.location.hash !== link.hash) window.history.pushState(null, '', link.hash);
      reveal(group.panel, true);
      group.title.focus({ preventScroll: true });
    }, options);
    input.addEventListener('input', render, options);
    window.addEventListener('hashchange', openHash, options);
    root.dataset.faqReady = 'true';
    nav.hidden = false;
    search.hidden = false;
    render();
    openHash();
    instances.set(root, {
      reveal,
      dispose() {
        controller.abort();
        cancelAnimationFrame(frame);
        entries.forEach(entry => { entry.original.append(entry.answer); entry.original.open = true; });
        list.replaceChildren(...originals);
        nav.hidden = true;
        search.hidden = true;
        empty.hidden = true;
        status.textContent = '';
        delete root.dataset.faqReady;
        root.classList.remove('yl-faq-searching');
        instances.delete(root);
      }
    });
  }
  function scan(scope = document) {
    if (scope.matches?.('[data-yl-faq]')) mount(scope);
    scope.querySelectorAll('[data-yl-faq]').forEach(mount);
  }
  window.YoLinkFaqController = { scan };
  scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of instances) if (event.target === root || event.target.contains(root)) instance.dispose();
  });
  document.addEventListener('shopify:block:select', event => {
    const item = event.target.closest('.yl-faq-item');
    const root = item?.closest('[data-yl-faq]');
    if (root) instances.get(root)?.reveal(item);
  });
})();
